import Vue from 'vue'
import Vuex from 'vuex'
import { configureScope } from '@sentry/browser'
import api from './api'

Vue.use(Vuex)

const mutate = (docs, doc) => {
  if (typeof (doc) === 'string') {
    const index = docs.findIndex((d) => d.id === doc)
    if (index !== -1) {
      docs.splice(index, 1)
    }
  } else {
    const index = docs.findIndex((d) => d.id === doc.id)
    if (index !== -1) {
      if (typeof (doc) === 'object') {
        Object.entries(doc).forEach(([key, value]) => Vue.set(docs[index], key, value))
      } else {
        Vue.set(docs, index, doc)
      }
    } else {
      docs.push(doc)
    }
  }
}

export default new Vuex.Store({
  state: {
    token: localStorage.getItem('token'),
    loading: false,
    initializing: false,
    devops: false,
    user: null,
    roleId: null,
    project: null,
    client: null,
    access: null,
    costs: {},
    tasks: [],
    instances: [],
    domains: [],
    applications: [],
    builds: [],
    repositories: [],
    teams: [],
    incidents: [],
    plans: [],
    packages: []
  },
  getters: {
    permission: (state) => (role, access) => {
      access = access || state.access
      if (access === null) {
        return false
      }
      switch (role) {
        case 'support': return access === 128
        case 'owner': return access === 1
        case 'admin': return access === 2
        case 'manager': return access === 4
        case 'developer': return access === 16
        case 'accounting': return access === 64
      }
    }
  },
  mutations: {
    init (state, data) {
      for (const key in data) {
        // eslint-disable-next-line no-prototype-builtins
        if (data.hasOwnProperty(key)) {
          Vue.set(state, key, data[key])
        }
      }
    },
    task (state, task) {
      mutate(state.tasks, task)
    },
    instance (state, instance) {
      mutate(state.instances, instance)
    },
    domain (state, domain) {
      mutate(state.domains, domain)
    },
    application (state, application) {
      mutate(state.applications, application)
    },
    build (state, build) {
      mutate(state.builds, build)
    },
    repository (state, repository) {
      mutate(state.repositories, repository)
    },
    team (state, team) {
      mutate(state.teams, team)
    },
    incident (state, incident) {
      mutate(state.incidents, incident)
    },
    reset (state) {
      state.costs = {}
      state.tasks = []
      state.instances = []
      state.domains = []
      state.applications = []
      state.builds = []
      state.repositories = []
      state.teams = []
      state.incidents = []
    },
    logout (state) {
      state.token = null
      state.loading = false
      state.initializing = false
      state.devops = false
      state.user = null
      state.project = null
      state.client = null
      state.access = null
      state.roleId = null
      delete Vue.api.defaults.headers.common.Authorization
      delete Vue.api.defaults.headers.common['x-runstack-role']
      localStorage.clear()
    }
  },
  actions: {
    ...api,
    role ({ commit, dispatch, state }, role) {
      configureScope((scope) => scope.setUser({
        email: state.user.email,
        project: role.project.name,
        projectId: role.project.id,
        clientId: role.project.client ? role.project.client.id : null,
        access: role.access
      }))

      Vue.api.defaults.headers.common['x-runstack-role'] = role.id
      localStorage.setItem('role', role.id)

      commit('init', {
        project: role.project,
        client: role.project.client,
        access: role.access,
        roleId: role.id
      })
    },
    async auth ({ commit, state, dispatch }) {
      Vue.api.defaults.headers.common.Authorization = 'Bearer ' + state.token
      localStorage.setItem('token', state.token)

      try {
        await dispatch('getUser')
      } catch (err) {
        return commit('logout')
      }

      if (state.user.roles.length > 0) {
        const role = state.user.roles.find((r) => r.id === localStorage.getItem('role')) || state.user.roles[0]
        dispatch('role', role)
      }
    },
    async load ({ commit, state, dispatch }, init) {
      if (state.loading || state.initializing) {
        return
      }
      if (init) {
        commit('init', { initializing: true })
      }

      commit('reset')
      commit('init', { loading: true })

      try {
        if (init) {
          await Promise.all([
            dispatch('getPackages'),
            dispatch('getPlans')
          ])
        }

        if (state.roleId) {
          await Promise.all([
            dispatch('getApplications'),
            dispatch('getDomains'),
            dispatch('getInstances'),
            dispatch('getIncidents'),
            dispatch('getRepositories'),
            dispatch('getBuilds'),
            dispatch('getTasks')
          ])
        }
      } catch (err) {
        return commit('logout')
      }

      commit('init', { loading: false })
      commit('init', { initializing: false })

      if (state.client && state.access <= 2) {
        dispatch('getTeams')
        dispatch('getBillingCosts')
      }
    }
  }
})
